import React, { useEffect, useMemo, useState } from 'react';
import {
  Tabs,
  Card,
  Typography,
  DatePicker,
  Select,
  Drawer,
  Switch,
  Segmented,
  Button,
  Popover,
  Tooltip,
} from 'antd';
import { FilterOutlined, SettingOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { getAllHubAppProfiles } from '../services/hubAppProfileService';
import { getAppEventsEnums } from '../services/appEventsService';
import dayjs from 'dayjs';
import AppEventsTable from '../components/analytics/AppEventsTable';
import AppEventsChartSection from '../components/analytics/AppEventsChartSection';
import AppStatsSection from '../components/analytics/AppStatsSection';

const { TabPane } = Tabs;
const { Text } = Typography;

const GRANULARITIES = [
  { value: 'day', label: 'Group by Day' },
  { value: 'week', label: 'Group by Week' },
  { value: 'month', label: 'Group by Month' },
  { value: 'quarter', label: 'Group by Quarter' },
  { value: 'year', label: 'Group by Year' },
];
const CHART_HEIGHT_OPTIONS = [240, 360, 480, 600, 720].map((value) => ({ label: `${value}px`, value }));
const CHART_SETTINGS_STORAGE_KEY = 'app-analytics-chart-settings';
const DEFAULT_CHART_SETTINGS = {
  height: 480,
  showDataLabels: false,
  lineCurve: 'straight',
};

const AppAnalytics = () => {
  const { appId } = useParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('app-stats');

  const { data: appProfiles = [] } = useQuery(['hub-app-profiles'], getAllHubAppProfiles, { staleTime: 5 * 60 * 1000 });

  const { data: analyticsEnums } = useQuery(['app-events-enums', appId], () => getAppEventsEnums({ appId }), {
    enabled: !!appId,
    staleTime: 5 * 60 * 1000,
  });
  const shopifyPlanOptions = useMemo(
    () => (analyticsEnums?.shopifyPlans || []).map((p) => ({ label: p, value: p })),
    [analyticsEnums?.shopifyPlans]
  );
  const countryOptions = useMemo(
    () => (analyticsEnums?.countries || []).map((c) => ({ label: c, value: c })),
    [analyticsEnums?.countries]
  );

  const appProfile = useMemo(() => appProfiles.find((p) => p.id === appId), [appProfiles, appId]);

  const requiredPermission = appProfile?.analytics?.required_permission;
  const canViewAppAnalytics = useMemo(() => {
    if (!appProfile?.analytics?.enabled) return false;
    if (!requiredPermission) return true;
    if (user?.role === 'admin') return true;
    return (user?.permissions || []).includes(requiredPermission);
  }, [appProfile, requiredPermission, user]);

  const defaultEnd = dayjs();
  const defaultStart = dayjs().subtract(6, 'day');
  const [dateRange, setDateRange] = useState([defaultStart, defaultEnd]);
  const [shopifyPlanScope, setShopifyPlanScope] = useState(undefined);
  const [countryScope, setCountryScope] = useState(undefined);
  const [granularity, setGranularity] = useState('day');
  const [chartSettingsOpen, setChartSettingsOpen] = useState(false);
  const [chartSettings, setChartSettings] = useState(DEFAULT_CHART_SETTINGS);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CHART_SETTINGS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setChartSettings((prev) => ({
        ...prev,
        ...(parsed && typeof parsed === 'object' ? parsed : {}),
      }));
    } catch (error) {
      // noop: fallback to defaults when localStorage is invalid/unavailable
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CHART_SETTINGS_STORAGE_KEY, JSON.stringify(chartSettings));
    } catch (error) {
      // noop: ignore localStorage write errors
    }
  }, [chartSettings]);

  if (!canViewAppAnalytics) {
    return (
      <Card style={{ margin: 24 }} title="App analytics">
        <Text type="secondary">Access denied.</Text>
      </Card>
    );
  }

  if (!appProfile) {
    return (
      <Card style={{ margin: 24 }} title="App analytics">
        <Text type="secondary">Unknown app.</Text>
      </Card>
    );
  }

  // Show filters on the same row as tab header.
  const tabBarExtraContent = (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}
    >
      <DatePicker.RangePicker
        value={dateRange}
        onChange={(value) => setDateRange(value || [null, null])}
        allowEmpty={[false, false]}
        format="YYYY-MM-DD"
        style={{ width: 260 }}
      />
      <Popover
        placement="bottomRight"
        trigger="click"
        content={
          <div style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Shopify plan</Text>
              </div>
              <Select
                allowClear
                placeholder="All plans"
                value={shopifyPlanScope}
                onChange={setShopifyPlanScope}
                options={shopifyPlanOptions}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
              />
            </div>
            <div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Country</Text>
              </div>
              <Select
                allowClear
                placeholder="All countries"
                value={countryScope}
                onChange={setCountryScope}
                options={countryOptions}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
              />
            </div>
            {activeTab === 'app-events' && (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Time bucket</Text>
                </div>
                <Select
                  value={granularity}
                  onChange={(v) => setGranularity(v)}
                  options={GRANULARITIES}
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>
        }
      >
        <Tooltip
          title={
            activeTab === 'app-events'
              ? 'Shopify plan, country, and chart time bucket'
              : 'Shopify plan and country'
          }
        >
          <Button type="default" icon={<FilterOutlined />} aria-label="Open filters" />
        </Tooltip>
      </Popover>
    </div>
  );

  const tabs = (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      tabBarExtraContent={tabBarExtraContent}
      renderTabBar={(props, DefaultTabBar) => (
        <div style={{ position: 'sticky', top: 0, zIndex: 50, background: '#fff', paddingTop: 8 }}>
          <DefaultTabBar {...props} />
        </div>
      )}
    >
      <TabPane tab="App Stats" key="app-stats">
        <AppStatsSection
          appId={appId}
          dateRange={dateRange}
          shopifyPlan={shopifyPlanScope}
          country={countryScope}
        />
      </TabPane>
      <TabPane tab="App Events" key="app-events">
        <div style={{ marginTop: 16 }}>
          <AppEventsTable
            appId={appId}
            dateRange={dateRange}
            scopeShopifyPlan={shopifyPlanScope}
            scopeCountry={countryScope}
          />
          <AppEventsChartSection
            appId={appId}
            dateRange={dateRange}
            granularity={granularity}
            chartSettings={chartSettings}
            shopifyPlan={shopifyPlanScope}
            country={countryScope}
          />
        </div>
      </TabPane>
      <TabPane tab="Install & Uninstall" key="install-uninstall">
        <div style={{ marginTop: 16 }} />
      </TabPane>
      <TabPane tab="Upgrade & Downgrade" key="upgrade-downgrade">
        <div style={{ marginTop: 16 }} />
      </TabPane>
    </Tabs>
  );

  // If app changes through navigation, keep the current tab.
  return (
    <div style={{ padding: 24 }}>
      {tabs}

      {activeTab === 'app-events' && (
        <Button
          type="primary"
          onClick={() => setChartSettingsOpen(true)}
          icon={<SettingOutlined />}
          style={{
            position: 'fixed',
            right: -42,
            top: '50%',
            transform: 'translateY(-50%) rotate(-90deg)',
            zIndex: 1000,
            borderRadius: '10px 10px 0 0',
            borderBottom: 'none',
            paddingInline: 16,
          }}
        >
          Chart settings
        </Button>
      )}

      <Drawer
        title="Chart settings"
        placement="right"
        width={320}
        onClose={() => setChartSettingsOpen(false)}
        open={chartSettingsOpen}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Chart height</div>
            <Select
              value={chartSettings.height}
              options={CHART_HEIGHT_OPTIONS}
              onChange={(value) =>
                setChartSettings((prev) => ({
                  ...prev,
                  height: Number(value) || 360,
                }))
              }
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600 }}>Show data labels</span>
            <Switch
              checked={chartSettings.showDataLabels}
              onChange={(checked) =>
                setChartSettings((prev) => ({
                  ...prev,
                  showDataLabels: checked,
                }))
              }
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Line style</div>
            <Segmented
              block
              options={[
                { label: 'Straight', value: 'straight' },
                { label: 'Curved', value: 'smooth' },
              ]}
              value={chartSettings.lineCurve}
              onChange={(value) =>
                setChartSettings((prev) => ({
                  ...prev,
                  lineCurve: value,
                }))
              }
            />
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default AppAnalytics;

